import type {ComponentType, ReactNode, ReactElement} from 'react';
import React, {Children, cloneElement} from 'react';
import {convertPxToRem} from '@pb/kit-util-convert-px-to-rem';

type GetRowDataResult = {
    children: ReactNode,
    wrappedCells: ReactElement | null,
};

export const getRowData = (
    props: {children?: ReactNode, height?: number, cellDefaults?: Record<string, unknown>},
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    RowComponent: ComponentType<any>,
): GetRowDataResult => {
    const cellItems: ReactElement[] = [];

    const children = Children.map(props.children, (child, key) => {
        if (!child) {
            return null;
        }
        const element = child as ReactElement<Record<string, unknown>>;
        if (element.type === RowComponent) {
            return cloneElement(element, {isHeaderRow: true});
        }
        // cellDefaults only fill in props the consumer has not explicitly set
        const defaults = Object.fromEntries(
            Object.entries(props.cellDefaults ?? {}).filter(([k]) => element.props[k] == null),
        );
        const heightProp = props.height != null ? {height: convertPxToRem(props.height)} : {};
        cellItems.push(cloneElement(element, {key, ...defaults, ...heightProp}));
        return null;
    });

    const wrappedCells = cellItems.length ? (
        <RowComponent isHeaderRow={true}>
            {cellItems}
        </RowComponent>
    ) : null;

    if (process.env.NODE_ENV === 'development' && cellItems.length) {
        // eslint-disable-next-line no-console
        console.warn(
            '[Table] Passing bare <Cell> children directly to <Head> or <Foot> is deprecated.\n' +
            'Wrap them in a <Row> instead:\n\n' +
            '  // Before (deprecated):\n' +
            '  <Head>\n' +
            '    <Cell>Company</Cell>\n' +
            '    <Cell>Amount</Cell>\n' +
            '  </Head>\n\n' +
            '  // After (preferred):\n' +
            '  <Head>\n' +
            '    <Row>\n' +
            '      <Cell as="th">Company</Cell>\n' +
            '      <Cell as="th">Amount</Cell>\n' +
            '    </Row>\n' +
            '  </Head>',
        );
    }

    return {children, wrappedCells};
};
